import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { makeStyles } from "@mui/styles";
import StyledTextField from "./StyledTextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useEffect } from "react";
import api from "../../api/config";
import { UpdatePatient, DeletePatient } from "../../api/Patient";
import ModalHeader from "./ModalHeader";
import { CopyAll } from "@mui/icons-material";
import { ModalFooter } from "./ModalFooter";
import { ActionButton } from "../shared/action-button";

const Content = styled(DialogContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "16px",
  backgroundColor: theme.colors.primaryBackground,
  color: "white",
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "16px",
}));

const useStyles = makeStyles({
  select: {
    "&:before": {
      borderColor: "white",
    },
    "&:after": {
      borderColor: "white",
    },
    "&:not(.Mui-disabled):hover::before": {
      borderColor: "white",
    },
  },
  icon: {
    fill: "white",
  },
  root: {
    color: "white",
  },
  label: {
    color: "white",
    "&.Mui-focused": {
      color: "white",
    },
  },
});

const EditPatientModal = ({ open, onClose, patient }) => {
  const [patientUuid, setPatientUuid] = useState(patient?.uuid ?? false);
  const [mammoCase, setMammoCase] = useState(patient?.mamo_case ?? false);
  const [legalCase, setLegalCase] = useState(patient?.legal_case ?? false);
  const [firstName, setFirstName] = useState(patient?.first_name || "");
  const [middleName, setMiddleName] = useState(patient?.middle_name || "");
  const [lastName, setLastName] = useState(patient?.last_name || "");
  const [insuranceMemberID, setInsuranceMemberID] = useState("");
  const [gender, setGender] = useState("Male");
  const [birthDate, setBirthDate] = useState(dayjs());
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [insurancePlanName, setInsurancePlanName] = useState("");
  const [insurancePlanCompany, setInsurancePlanCompany] = useState("");
  const [subscriberFirstName, setSubscriberFirstName] = useState("");
  const [subscriberLastName, setSubscriberLastName] = useState("");
  const [groupNumber, setGroupNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleToggleChange = (type, value) => {
    if (type === "mammo") setMammoCase(value);
    if (type === "legal") setLegalCase(value);
  };

  const handleSave = async () => {
    const updatedPatient = {
      ...patient,
      uuid: patientUuid,
      mamo_case: mammoCase,
      legal_case: legalCase,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      insurance_member_id: insuranceMemberID,
      gender,
      date_of_birth: birthDate.format("YYYY-MM-DD"),
      address_street: street,
      address_city: city,
      address_state: state,
      address_zip: zipCode,
      insurance_plan_name: insurancePlanName,
      insurance_plan_company: insurancePlanCompany,
      insurance_subscriber_first_name: subscriberFirstName,
      insurance_subscriber_last_name: subscriberLastName,
      insurance_group_number: groupNumber,
    };
    setLoading(true);
    try {
      console.log("Updated Patient: ", updatedPatient);

      const response = await api.put(
        `${UpdatePatient}/${updatedPatient?.uuid}`,
        updatedPatient
      );

      console.log("Response: ", response);

      if (response && response.status === 200) {
        console.log("Patient updated successfully");
        alert("Patient updated successfully");
        onClose();
      } else {
        alert("Failed to update patient");
      }
    } catch (error) {
      console.error("Error updating patient:", error);
      alert("An error occurred while updating the patient. Please try again.");
    }
    setLoading(false);
  };

  const sanitizeText = (text) => {
    return text ? text.replace(/\s+/g, "") : "";
  };

  useEffect(() => {
    console.log("Patient: ", patient);
    setPatientUuid(sanitizeText(patient?.uuid) || "");
    setMammoCase(patient?.mamo_case || false);
    setLegalCase(patient?.legal_case || false);
    setFirstName(patient?.first_name || "");
    setMiddleName(patient?.middle_name || "");
    setLastName(patient?.last_name || "");
    setInsuranceMemberID(patient?.insurance_member_id || "");
    setGender(patient?.gender || "");
    setBirthDate(dayjs(patient?.date_of_birth));
    setStreet(patient?.address_street || "");
    setCity(patient?.address_city || "");
    setState(patient?.address_state || "");
    setZipCode(sanitizeText(patient?.address_zip) || "");
    setInsurancePlanName(patient?.insurance_plan_name || "");
    setInsurancePlanCompany(patient?.insurance_plan_company || "");
    setSubscriberFirstName(patient?.insurance_subscriber_first_name || "");
    setSubscriberLastName(patient?.insurance_subscriber_last_name || "");
    setGroupNumber(patient?.insurance_group_number || "");
  }, [patient]);

  const deletePatient = async () => {
    setLoading(true);
    try {
      const response = await api.delete(`${DeletePatient}/${patientUuid}`);
      if (response && response.status === 200) {
        console.log("Patient deleted successfully");
        alert("Patient deleted successfully");
        onClose();
      } else {
        alert("Failed to delete patient");
      }
    } catch (error) {
      console.error("Error deleting patient:", error);
      alert("An error occurred while deleting the patient. Please try again.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <ModalHeader
        title={`Edit Patient - ${firstName} ${lastName}`}
        onClose={onClose}
      />
      <Content>
        <Row>
          <StyledTextField
            label="Movu Patient ID"
            variant="outlined"
            fullWidth
            value={patientUuid}
            disabled
            sx={{
              "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
                borderColor: "white",
                color: "#ffffff",
              },
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <CopyAll
              onClick={() => {
                navigator.clipboard.writeText(patientUuid);
              }}
              style={{ cursor: "pointer" }}
            />
          </div>
        </Row>

        <Row>
          <StyledTextField
            label="First Name"
            variant="outlined"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <StyledTextField
            label="Middle Name"
            variant="outlined"
            fullWidth
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
          />
          <StyledTextField
            label="Last Name"
            variant="outlined"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Row>

        <StyledTextField
          label="Insurance Member ID"
          variant="outlined"
          fullWidth
          value={insuranceMemberID}
          onChange={(e) => setInsuranceMemberID(e.target.value)}
        />

        <StyledTextField
          select
          className={classes.select}
          label="Gender"
          variant="outlined"
          fullWidth
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#141b25",
                  color: "#FBFEFF",
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            },
          }}
          sx={{
            color: "white",
            "& .MuiInputBase-input": {
              height: "20px",
              lineHeight: "20px",
              minHeight: "20px",
            },
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: "#A1A5AC",
              borderRadius: "4px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#A1A5AC",
              borderRadius: "4px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#A1A5AC",
              borderRadius: "4px",
            },
            ".MuiSvgIcon-root ": {
              fill: "white !important",
            },
          }}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </StyledTextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date of Birth"
            value={birthDate}
            onChange={(newValue) => setBirthDate(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Date of Birth"
                variant="outlined"
                fullWidth
                sx={{ backgroundColor: "black" }}
              />
            )}
            slotProps={{
              layout: {
                sx: {
                  color: "#000000",
                  backgroundColor: "#3E526A",
                },
              },
            }}
            sx={{
              width: "100%",
              "& .MuiPickersLayout-root": {
                backgroundColor: "#141b25 !important",
              },
              "& .MuiInputLabel-root": {
                color: "#ffffff",
                borderRadius: "12px",
                borderColor: "#A1A5AC",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#ffffff",
                borderColor: "#F0F5F7",
              },
              "& .MuiOutlinedInput-root": {
                "& > fieldset": { borderColor: "#A1A5AC" },
                "&:hover > fieldset": { borderColor: "#A1A5AC" },
                "&.Mui-focused > fieldset": { borderColor: "#A1A5AC" },
              },
              "& .MuiInputBase-input": {
                height: "20px",
              },
              "& .MuiSvgIcon-root": {
                fill: "white !important",
              },
              "& .MuiPaper-root": {
                backgroundColor: "#141b25",
              },
            }}
          />
        </LocalizationProvider>

        <StyledTextField
          label="Street"
          variant="outlined"
          fullWidth
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />

        <Row>
          <StyledTextField
            label="City"
            variant="outlined"
            fullWidth
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <StyledTextField
            label="State"
            variant="outlined"
            fullWidth
            value={state}
            onChange={(e) => setState(e.target.value)}
          />
          <StyledTextField
            label="Zip Code"
            variant="outlined"
            fullWidth
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
          />
        </Row>

        <StyledTextField
          label="Insurance Plan Name"
          variant="outlined"
          fullWidth
          value={insurancePlanName}
          onChange={(e) => setInsurancePlanName(e.target.value)}
        />
        <StyledTextField
          label="Insurance Plan Company"
          variant="outlined"
          fullWidth
          value={insurancePlanCompany}
          onChange={(e) => setInsurancePlanCompany(e.target.value)}
        />
        <StyledTextField
          label="Insurance Subscriber First Name"
          variant="outlined"
          fullWidth
          value={subscriberFirstName}
          onChange={(e) => setSubscriberFirstName(e.target.value)}
        />
        <StyledTextField
          label="Insurance Subscriber Last Name"
          variant="outlined"
          fullWidth
          value={subscriberLastName}
          onChange={(e) => setSubscriberLastName(e.target.value)}
        />
        <StyledTextField
          label="Insurance Group Number"
          variant="outlined"
          fullWidth
          value={groupNumber}
          onChange={(e) => setGroupNumber(e.target.value)}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "8px",
          }}
        >
          <Button
            variant="text"
            style={{ color: "red", fontWeight: "bold" }}
            onClick={() => setIsDeleteConfirmOpen(true)}
          >
            Delete Patient
          </Button>
        </div>
      </Content>
      <ModalFooter>
        <ActionButton variantType="outlined" colorType="negative" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton onClick={handleSave} color="primary" variant="contained">
          Save
        </ActionButton>
      </ModalFooter>
      <Backdrop open={loading} style={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
      >
        <Content style={{ textAlign: "center" }}>
          <p style={{ fontSize: "18px", fontWeight: "bold" }}>
            Delete Patient? This action cannot be undone!
          </p>
          <DialogActions sx={{ justifyContent: "center" }}>
            <ActionButton
              onClick={() => setIsDeleteConfirmOpen(false)}
              variantType="filled"
              colorType="primary"
            >
              No
            </ActionButton>
            <ActionButton
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                deletePatient();
              }}
              variantType="filled"
              colorType="negative"
            >
              Yes
            </ActionButton>
          </DialogActions>
        </Content>
      </Dialog>
    </Dialog>
  );
};

export default EditPatientModal;
